import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { motion, useDragControls } from 'framer-motion';
import get from 'lodash/get';

const modalStates = {
  visible: {
    opacity: 1,
    display: 'flex'
  },
  hidden: {
    opacity: 0,
    transitionEnd: {
      display: 'none'
    }
  },
};

const modalContentStates = {
  visible: {
    opacity: 1,
    top: 0
  },
  hidden: {
    opacity: 0,
    top: 500,
    transitionEnd: {
      top: 100,
    }
  }
};

export const Modal = function ({ id, header, children, isOpen, onClose }) {
  const { __CLIENT_HYDRATED__, modals, openedModals } = useSelector((state) => state.layout, shallowEqual);

  const dispatch = useDispatch();
  const modalRef = useRef(null);

  const [dragConstraints, setDragConstraints] = useState({ top: 0, bottom: 0 });
  const [modalYPosition, setModalYPosition] = useState(0);
  const [scrollYPosition, setScrollYPosition] = useState(0);
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  useEffect(() => {
    if (__CLIENT_HYDRATED__ && modals.indexOf(id) === -1) {
      dispatch({ type: 'MODAL_MOUNTED', payload: id });
      setIsMobileDevice(
        ('ontouchstart' in window) ||
        (navigator.maxTouchPoints > 0) ||
        (navigator.msMaxTouchPoints > 0)
      );
    }
  }, [__CLIENT_HYDRATED__, modals]);

  useEffect(() => {
    if (modalRef.current && modalRef.current.getAttribute('listener') !== 'true') {
      const clickFn = (e) => {
        if (e.target.classList.contains('backdrop')) {
          onClose();
        }
      };

      modalRef.current.setAttribute('listener', 'true');
      modalRef.current.addEventListener('click', clickFn);

      return () => {
        if (modalRef.current) {
          modalRef.current.setAttribute('listener', 'false');
          modalRef.current.removeEventListener('click', clickFn);
        }
      };
    }
  }, [modalRef, modals]);

  useEffect(() => {
    if (isOpen && openedModals.indexOf(id) === -1) {
      dispatch({ type: 'MODAL_OPENED', payload: id });
    } else if (!isOpen && openedModals.indexOf(id) !== -1) {
      dispatch({ type: 'MODAL_CLOSED', payload: id });
    }
  }, [isOpen, openedModals]);

  const dragControls = useDragControls()

  function startDrag(event) {
    dragControls.start(event, { snapToCursor: false });
  }

  function computeConstraints() {
    if (modalRef.current) {
      const scrollEl = get(modalRef.current.getElementsByClassName('scrollable'), '[0]');
      if (scrollEl) {
        setDragConstraints({
          top: -(scrollEl.offsetHeight - scrollEl.parentElement.offsetHeight),
          bottom: 0
        });
      }
    }
  }

  function propagateDragging(values) {
    setScrollYPosition(values.y);

    if (modalRef.current && values.y && values.y >= 0) {
      setModalYPosition(values.y);
    }
  }

  function onDragEndHandler(event, info, ignoreScroll) {
    const { velocity } = info;

    if ((scrollYPosition >= 0 || ignoreScroll) && velocity.y >= 2500) {
      onClose();
    }
  }

  return (modals.indexOf(id) !== -1) && createPortal(
    (
      <motion.div initial="hidden" variants={modalStates} animate={isOpen ? 'visible' : 'hidden'} id={id} ref={modalRef} className={`fixed w-full h-full sm:px-8 bg-stone-900/70 left-0 top-0 justify-center backdrop-blur-sm backdrop`}>
        <motion.div drag="y" dragElastic={1} style={{ y: modalYPosition }} onDragEnd={(e, info) => { onDragEndHandler(e, info, true) }} dragConstraints={{ bottom: 0, top: 0 }} dragControls={dragControls} dragListener={false} variants={modalContentStates} onPointerDown={startDrag} className="bg-white relative rounded-t w-full self-end pb-4 max-h-[90%] sm:pb-0 sm:h-auto sm:max-w-[960px] sm:self-center sm:rounded-b flex flex-col overflow-hidden drop-shadow-xl touch-none wrapper">
          {
            header
              ? (
                <motion.div className="w-full bg-white border-0 border-b border-solid border-stone-100 p-4 flex items-center touch-none">
                  <motion.div className="grow">
                    {header}
                  </motion.div>
                  <motion.div whileHover={{ rotate: 90 }} whileTap={{ scale: 0.8 }} className="cursor-pointer flex items-center justify-center w-8 h-8">
                    <XMarkIcon width={36} onClick={onClose}/>
                  </motion.div>
                </motion.div>
              )
              : (
                <motion.div whileHover={{ rotate: 180 }} className="bg-white rounded-full flex items-center justify-center w-8 h-8 absolute right-3 top-3 drop-shadow">
                <XMarkIcon width={36} onClick={onClose}/>
                </motion.div>
              )
          }
          <motion.div
            className="grow w-full overflow-y-auto"
            onPointerDownCapture={e => {
              if (!isMobileDevice) {
                e.stopPropagation();
              }
            }}>
            {
              isMobileDevice
                ? (
                  <motion.div drag="y" dragElastic={0.1} onUpdate={propagateDragging} onPointerDown={computeConstraints} onDragEnd={onDragEndHandler} dragConstraints={dragConstraints} className="w-full p-4 touch-none scrollable">
                    {children}
                  </motion.div>
                )
                : (
                  <motion.div className="w-full p-4 scrollable">
                    {children}
                  </motion.div>
                )
            }
          </motion.div>
        </motion.div>
      </motion.div>
    ),
    document.getElementById('modals-container')
  );
};
